import React from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Form,
    Input,
    Progress,
    Row,
    Select,
    Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import { SettingOutlined, UploadOutlined } from '@ant-design/icons';
import { AppApi } from '../../../services/Api';
import { DisplayOptions, FIRMWARE_VERSION, ROLE } from '../../../services/Macro';

type DisplayOption = (typeof DisplayOptions)[keyof typeof DisplayOptions];

interface AppPanel4IosProps {
    confirm: string;
    trans: (key: string) => string;
    handleError: (title: string, info: string) => void;
    updateFileList: (upFm: boolean, upApp: boolean) => void;
    notify: (message: string) => void;
}

interface AppPanel4IosState {
    appPanelDisplay: DisplayOption;
    version: string;
    appTypes: string[];
    versionNo: string;
    versionName: string;
    log: string;
    logEng: string;
    downloadUrl: string;
    progress: number;
    fileList: UploadFile[];
}

interface FormValues {
    versionNo: string;
    versionName: string;
    log: string;
    logEng: string;
    downloadUrl: string;
}

export default class AppPanel4Ios extends React.Component<AppPanel4IosProps, AppPanel4IosState> {
    state: AppPanel4IosState = {
        appPanelDisplay: DisplayOptions.block,
        version: 'Beta',
        appTypes: [] as string[],
        versionNo: '',
        versionName: '',
        log: '',
        logEng: '',
        downloadUrl: '',
        progress: 0,
        fileList: [] as UploadFile[],
    };

    switchAppUpgradePanel = () => {
        this.setState((prev) => ({
            appPanelDisplay:
                prev.appPanelDisplay === DisplayOptions.none
                    ? DisplayOptions.block
                    : DisplayOptions.none,
        }));
    };

    callApi = async (values: FormValues) => {
        const { trans: t, handleError, notify } = this.props;
        const { appTypes, version, fileList } = this.state;

        if (appTypes.length === 0) {
            handleError(t('localError'), t('appTypeErr'));
            return;
        }
        if (!values.versionNo) {
            handleError(t('localError'), t('versionNoNull'));
            return;
        }
        if (!values.versionName) {
            handleError(t('localError'), t('versionNameNull'));
            return;
        }
        if (!values.log) {
            handleError(t('localError'), t('logNull'));
            return;
        }
        if (!values.logEng) {
            handleError(t('localError'), t('logEngNull'));
            return;
        }
        if (fileList.length === 0 || !fileList[0].originFileObj) {
            handleError(t('localError'), t('upgradeFileNull'));
            return;
        }

        const token = localStorage.getItem('token') || '';
        const lang = localStorage.getItem('lang') || 'zh_CN';
        const formData = new FormData();

        formData.append('token', token);
        formData.append('role', String(ROLE.SUPER));
        formData.append('appVersion', String((FIRMWARE_VERSION as Record<string, unknown>)[version]));
        formData.append('appType', `[${appTypes}]`);
        formData.append('downloadUrl', values.downloadUrl || '');
        formData.append('versionNo', values.versionNo);
        formData.append('versionName', values.versionName);
        formData.append('log', values.log);
        formData.append('logEng', values.logEng);

        fileList.forEach((item) => {
            if (item.originFileObj) {
                formData.append('files', item.originFileObj);
            }
        });
        formData.append('lang', lang.split('_')[0]);

        try {
            const upload = axios.create({
                baseURL: '',
                onUploadProgress: (event) => {
                    if (event.total) {
                        this.setState({ progress: Math.round((event.loaded / event.total) * 100) });
                    }
                },
            });

            const response = await upload.post(AppApi.SUPER_UPGRADE_IOS_TEST, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.code === 400) {
                handleError(t('serverError'), response.data.message);
                return;
            }

            this.setState({ progress: 0, fileList: [] });
            notify(t('upgradeSuccess'));
        } catch (error: any) {
            this.setState({ progress: 0 });
            handleError(t('serverError'), error?.response?.data?.message || error?.message || String(error));
        }
    };

    render() {
        const { confirm, trans: t } = this.props;
        const { appPanelDisplay, version, appTypes, progress, fileList } = this.state;

        return (
            <Card
                title={t('ios') + t('appUpgrade')}
                extra={<Button type="text" icon={<SettingOutlined />} onClick={this.switchAppUpgradePanel} />}
                style={{ marginBottom: 16 }}
            >
                {appPanelDisplay !== DisplayOptions.none && (
                    <Form<FormValues> layout="vertical" onFinish={this.callApi}>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label={t('upgradeVersion')}>
                                    <Select
                                        value={version}
                                        onChange={(value) => this.setState({ version: value })}
                                        options={[{ value: 'Beta', label: 'Beta' }]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={t('appVersion')}>
                                    <Checkbox.Group
                                        value={appTypes}
                                        options={[
                                            { value: '1', label: 'dev' },
                                            { value: '2', label: 'alpha' },
                                            { value: '3', label: 'beta' },
                                            { value: '4', label: 'pro' },
                                        ]}
                                        onChange={(values) => this.setState({ appTypes: values as string[] })}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="versionNo" label={t('versionNo')} rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="versionName" label={t('versionName')} rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="log" label={t('chinese') + 'LOG'} rules={[{ required: true }]}>
                                    <Input.TextArea rows={5} placeholder={t('typeYourMsg')} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="logEng" label={t('english') + 'LOG'} rules={[{ required: true }]}>
                                    <Input.TextArea rows={5} placeholder={t('typeYourMsg')} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="downloadUrl" label={t('downloadUrl')}>
                                    <Input.TextArea rows={2} placeholder={t('typeDownloadUrl')} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Upload
                                    multiple
                                    fileList={fileList}
                                    beforeUpload={() => false}
                                    onChange={({ fileList: next }) => this.setState({ fileList: next })}
                                >
                                    <Button icon={<UploadOutlined />}>Select Files</Button>
                                </Upload>
                                <Progress percent={progress} status={progress === 100 ? 'success' : 'active'} />
                            </Col>
                            <Col span={24} style={{ textAlign: 'right' }}>
                                <Button type="primary" htmlType="submit">{confirm}</Button>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Card>
        );
    }
}
